import { core } from "@tauri-apps/api";
import { useState, useEffect } from "react";

interface FreezeInfoData {
  is_auto_freeze: boolean;
  is_manual_freeze: boolean;
  reason: null | string;
}

/**
 * WHAT:
 *   Optimized Freeze Info Hook that calls Tauri command directly.
 *
 * WHY:
 *   Calls the Tauri command directly for freeze info data.
 *
 * @returns Object containing freeze info data, loading state, and error
 *
 */
export const useOptimizedFreezeInfo = () => {
  const [data, setData] = useState<FreezeInfoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);

  useEffect(() => {
    const fetchFreezeInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await core.invoke<FreezeInfoData>("get_today_freeze_info");
        setData(result);
      } catch (error_) {
        setError((error_ as Error).message);
      } finally {
        setLoading(false);
      }
    };

    void fetchFreezeInfo();
  }, []);

  return {
    endTime: null, // Not available from this endpoint
    error,
    freezeInfo: data,
    is_auto_freeze: data?.is_auto_freeze ?? false,
    is_manual_freeze: data?.is_manual_freeze ?? false,
    loading,
    reason: data?.reason ?? null,
    startTime: null, // Not available from this endpoint
  };
};
